import {AccountEntityModel} from "../account-entity.model";
import {AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, Optional, ViewChild} from "@angular/core";
import {EntityData, provideEntityModel} from "@fedeltapointofsale/data-client";
import {AbstractEditPage} from "../../../framework/entity/edit/edit-page/abstract-edit-page";
import {EditGridComponent} from "../../../framework/entity/edit/edit-grid/edit-grid.component";
import {ContactSearchPageComponent} from "../../../contact/contact-search-page/contact-search-page.component";
import {ContactEditPageComponent} from "../../../contact/contact-edit-page/contact-edit-page.component";
import {
    FUIAlertActions,
    FUIAlertService,
    FUIResponsiveService,
    FUIStackedPageContextService,
    FUIStackedPageService
} from "@fedeltapointofsale/ui";
import {AccountData, AccountPinData} from "../account.data";
import {FormControl} from "@angular/forms";
import {EntityFormControl} from "../../../framework/entity/edit/entity-form-builder/entity-form-control";
import {EditMode} from "../../../framework/entity/edit/edit-page/edit-mode";
import {Subscription} from "rxjs";
import {
    AccountPaymentSearchPageComponent
} from "../../account-payment/account-payment-search-page/account-payment-search-page.component";
import {ExtendedSearchParams, SearchType} from "../../../framework/entity/search/search-page/extended-search-params";
import {AccountTypeData} from "../../account-type/account-type.data";
import {AccountClearBalanceAction, AccountClearBalanceService} from "../account-clear-balance.service";
import {
    ProductPriceLevelReportPageComponent
} from "../../../catalogue/reports/productpricelevel/product-price-level-report-page.component";
import {ReportParams} from "../../../framework/entity/report/report-page/report-params";

export enum NextAction {
    PAYMENT_MANAGEMENT,
    CLEAR_BALANCE
}

@Component({
    selector: "wbo-account-edit-page",
    templateUrl: "./account-edit-page.component.html",
    styleUrls: ["./account-edit-page.component.scss"],
    providers: [(provideEntityModel(AccountEntityModel)), AccountClearBalanceService]
})
export class AccountEditPageComponent extends AbstractEditPage implements AfterViewInit, OnInit, OnDestroy {

    EditMode = EditMode;

    ContactSearchPageComponent = ContactSearchPageComponent;
    ContactEditPageComponent = ContactEditPageComponent;

    fromContactEditScreen: boolean;

    chargeDate: Date;
    chargeAmt: number;
    paymentDate: Date;
    paymentAmt: number;

    /**
     * Action to perform after a save action
     */
    nextAction: NextAction;
    @ViewChild(EditGridComponent) editGridComponent: EditGridComponent;
    private subscriptions: Subscription = new Subscription();

    constructor(public model: AccountEntityModel,
                @Optional() private stackedPageContextService: FUIStackedPageContextService,
                private stackedPageService: FUIStackedPageService,
                private alertService: FUIAlertService,
                private clearBalanceService: AccountClearBalanceService,
                changeDetectorRef: ChangeDetectorRef,
                responsiveService: FUIResponsiveService) {
        super(changeDetectorRef, responsiveService);
    }

    get accountNoAuto(): boolean {
        if (this.model.data.accountNoAuto == null) {
            this.model.data.accountNoAuto = true;
        }
        return this.model.data.accountNoAuto;
    }

    set accountNoAuto(accountNoAuto: boolean) {
        this.model.data.accountNoAuto = accountNoAuto;
        this.editPageComponent.editService.formGroup.get("details").get("accountNo").reset(null);
    }

    get isControlsReadonly(): boolean {
        return (<EntityFormControl>this.editPageComponent.editService.formGroup.get("details").get("accountName")).restricted;
    }

    get invoiceIssuedContact(): number {
        const invoiced: AccountPinData = this.model.data.accountPins.find((pin) => pin.invoiceContact);
        return invoiced != undefined ? invoiced.accountPinId : -1;
    }

    set invoiceIssuedContact(value: number) {
        this.model.data.accountPins.forEach((pin) => {
            pin.invoiceContact = value !== -1 && pin.accountPinId === value;
        });
    }

    get statementIssuedContact(): number {
        const invoiced: AccountPinData = this.model.data.accountPins.find((pin) => pin.statementContact);
        return invoiced != undefined ? invoiced.accountPinId : -1;
    }

    set statementIssuedContact(value: number) {
        this.model.data.accountPins.forEach((pin) => {
            pin.statementContact = value !== -1 && pin.accountPinId === value;
        });
    }

    get invoiceIssuedContactName(): string {
        let invoiced: AccountPinData = this.model.data.accountPins.find((pin) => pin.invoiceContact);
        if (invoiced) {
            return invoiced.contact.fullName;
        }
        invoiced = this.model.data.accountPins.find((pin) => pin.defaultContact);
        if (invoiced) {
            return invoiced.contact.fullName;
        }
        return "unknown";
    }

    get statementIssuedContactName(): string {
        let invoiced: AccountPinData = this.model.data.accountPins.find((pin) => pin.statementContact);
        if (invoiced) {
            return invoiced.contact.fullName;
        }
        invoiced = this.model.data.accountPins.find((pin) => pin.defaultContact);
        if (invoiced) {
            return invoiced.contact.fullName;
        }
        return "unknown";
    }

    get billingCycleDisplay(): string {
        return "{{wbo.account.edit.billingcycle.option." + (this.model.data.invoiceDetails.billingCycleDbValue === AccountTypeData.BILLING_CYCLE_NONE ? AccountTypeData.BILLING_CYCLE_MONTHLY : this.model.data.invoiceDetails.billingCycleDbValue) + "}}";
    }

    ngOnInit() {
        this.editPageComponent.editService.formGroup.addControl("accountNoAuto", new FormControl());
        this.subscriptions.add(this.model.$componentLoaded("details.accountType").subscribe(() => this.accountTypeLoaded()));

        this.subscriptions.add(this.model.$data.subscribe(() => {
            if (this.model.data.accountHistoryData.lastTrx && this.model.data.accountHistoryData.lastTrx.length > 0) {
                this.chargeDate = this.model.data.accountHistoryData.lastTrx[0].chargeDate;
                this.chargeAmt = this.model.data.accountHistoryData.lastTrx[0].chargeAmt;
            }
            if (this.model.data.accountHistoryData.lastPayment && this.model.data.accountHistoryData.lastPayment.length > 0) {
                this.paymentDate = this.model.data.accountHistoryData.lastPayment[0].details.paymentDate;
                this.paymentAmt = this.model.data.accountHistoryData.lastPayment[0].details.paymentAmt;
            }
        }));
    }

    ngAfterViewInit(): void {
        if (this.stackedPageContextService && this.stackedPageContextService.params.parentEntity) {
            const accountPin: AccountPinData = <AccountPinData>EntityData.create(AccountPinData);
            accountPin.defaultContact = true;
            accountPin.contact = this.stackedPageContextService.params.parentEntity;
            this.editGridComponent.addRow(accountPin);
            this.fromContactEditScreen = true;
        }

        this.subscriptions.add(this.editPageComponent.saveFailed.subscribe(() => {
            this.nextAction = undefined;
        }));

        this.subscriptions.add(this.editPageComponent.saved.subscribe(() => {
            if (this.nextAction === NextAction.CLEAR_BALANCE) {
                this.processClearBalance();
            } else if (this.nextAction === NextAction.PAYMENT_MANAGEMENT) {
                this.goToPaymentManagement();
            }
        }));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    afterEntityAdded(accountPinData: AccountPinData): void {
        accountPinData.defaultContact = this.model.data.accountPins.length === 0;
    }

    onSavedAccountPin(accountPinData: AccountPinData): void {
        if (accountPinData.defaultContact) {
            this.model.data.accountPins.forEach((pin) => {
                if (pin !== accountPinData) {
                    pin.defaultContact = false;
                }
            });
        }
    }

    accountTypeLoaded(): void {
        if (this.editPageComponent.editService.editMode === EditMode.ADD && this.model.data.details.accountType.validBillingCycles.find((value) => value === this.model.data.invoiceDetails.billingCycleDbValue) === undefined) {
            this.model.data.invoiceDetails.billingCycleDbValue = this.model.data.details.accountType.validBillingCycles[0];
        }
    }

    managePayments(): void {
        if (this.editPageComponent.formGroup.dirty) {
            this.alertService.showConfirmationOkCancel("wbo.account.edit.prompt.paymentNotSaved.heading",
                "wbo.account.edit.prompt.paymentNotSaved.message").subscribe((action) => {
                if (action === FUIAlertActions.OK_ACTION) {
                    this.nextAction = NextAction.PAYMENT_MANAGEMENT;
                    this.editPageComponent.saveAndStay();
                } else {
                    this.nextAction = undefined;
                }
            });
        } else {
            this.goToPaymentManagement();
        }
    }

    /**
     * Indicates if the Account supports payments
     */
    supportsPayment(): boolean {
        return !this.model.data.details.discontinued &&
        (this.model.data.details.accountType.accountingType === AccountTypeData.DEBTOR || this.model.data.details.accountType.accountingType === AccountTypeData.VOUCHER);
    }

    clearBalance(): void {
        this.alertService.showConfirmationOkCancel("wbo.account.edit.prompt.clearBalance.heading",
            "wbo.account.edit.prompt.clearBalance.message").subscribe((action) => {
            if (action === FUIAlertActions.OK_ACTION) {
                this.clearBalanceDirtyCheckAndProcess();
            }
        });
    }

    clearBalanceDirtyCheckAndProcess(): void {
        if (this.editPageComponent.formGroup.dirty) {
            this.alertService.showConfirmationOkCancel("wbo.account.edit.prompt.balanceNotSaved.heading",
                "wbo.account.edit.prompt.balanceNotSaved.message").subscribe((action) => {
                if (action === FUIAlertActions.OK_ACTION) {
                    this.nextAction = NextAction.CLEAR_BALANCE;
                    this.editPageComponent.saveAndStay();
                } else {
                    this.nextAction = undefined;
                }
            });
        } else {
            this.processClearBalance();
        }
    }

    processClearBalance(): void {
        this.nextAction = undefined;
        this.clearBalanceService.performAction(new AccountClearBalanceAction(this.model.data.accountId)).then(() => {
            this.editPageComponent.reload();
        });
    }

    priceBookReport(): void {
        this.stackedPageService.open(ProductPriceLevelReportPageComponent, new ReportParams(this.model.data.details.priceLevel));
    }

    addingAnother(previousEntity: AccountData): void {
        this.model.data.details.accountType.id = previousEntity.details.accountType.id;
    }

    private goToPaymentManagement(): void {
        this.nextAction = undefined;
        this.subscriptions.add(this.stackedPageService.open(AccountPaymentSearchPageComponent,
            new ExtendedSearchParams(SearchType.NO_SELECTION, {
                accountId: this.model.data.accountId,
                accountName: this.model.data.accountName
            })).subscribe(() => {
        }, () => {
        }, () => {
            this.editPageComponent.reload()
        }));
    }
}
